import React, { useState } from "react";
import { Box, Stack, Button, CircularProgress, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";

import AddEditBusDialog from "../../../Transport/jsx/dialogs/AddEditBusDialog";
import CopyBusDialog from "../../../Transport/jsx/dialogs/CopyBusDialog";
import AddTicketBlockDialog from "../../dialogs/AddTicketBlockDialog";
import JourneysPanel from "../../../Transport/jsx/boards/JourneysPanel";
import { can } from "../../../shared utils/auth";

/**
 * Presentational transport "Journeys" column for the Activities board. All data
 * and handlers come from the board's useActivityTransport() hook (passed in as
 * `transport`) so this column shares state with the activity-group column,
 * letting members be assigned straight onto a bus.
 *
 * Note: this is the journeys (right-hand) column only — assigning brand-new
 * passengers from the candidate pool still lives on the dedicated Transport
 * view. Here you can add/edit/delete buses, clear/remove passengers, and assign
 * activity-group members from the middle column.
 */
export default function ActivityTransportColumn({
  selectedCampusId,
  campusList = [],
  transport,
}) {
  const {
    journeys,
    loading,
    error,
    campDateStr,
    selectedCampusLabel,
    airports,
    busDialogOpen,
    editingJourney,
    openAddBus,
    openEditBus,
    closeBusDialog,
    handleSaveBus,
    createTicketBlock,
    handleDeleteJourney,
    savePassengersForBus,
    confirmJourney,
    refreshJourneys,
  } = transport;

  const [copyBusOpen, setCopyBusOpen] = useState(false);
  const [ticketBlockOpen, setTicketBlockOpen] = useState(false);

  // Same rule as the Transport board: reading is USER, changing needs the grant.
  const canEdit = can("transport");

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1, minHeight: 36 }}
      >
        {canEdit && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openAddBus}
              disabled={loading || !campDateStr}
            >
              Add bus
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => setCopyBusOpen(true)}
              disabled={loading || !campDateStr}
            >
              Copy bus
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<ConfirmationNumberIcon />}
              onClick={() => setTicketBlockOpen(true)}
              disabled={loading || !campDateStr || !selectedCampusLabel}
            >
              Add ticket block
            </Button>
          </>
        )}

        {loading && <CircularProgress size={16} />}

        {error && (
          <Typography variant="body2" color="error">
            {String(error?.message || error)}
          </Typography>
        )}
      </Stack>

      <AddEditBusDialog
        open={busDialogOpen}
        onClose={closeBusDialog}
        campDate={campDateStr}
        campuses={campusList}
        servedAirports={airports}
        defaultCampusId={selectedCampusId}
        journey={editingJourney}
        onSave={handleSaveBus}
      />

      <CopyBusDialog
        open={copyBusOpen}
        onClose={() => setCopyBusOpen(false)}
        targetDate={campDateStr}
        onCopied={async () => {
          await refreshJourneys?.();
        }}
      />

      <AddTicketBlockDialog
        open={ticketBlockOpen}
        onClose={() => setTicketBlockOpen(false)}
        campDate={campDateStr}
        campusName={selectedCampusLabel}
        onCreate={createTicketBlock}
      />

      <JourneysPanel
        selectedCampusId={selectedCampusId}
        selectedCampusLabel={selectedCampusLabel}
        filteredJourneys={journeys}
        onEditJourney={canEdit ? openEditBus : undefined}
        onDeleteJourney={canEdit ? handleDeleteJourney : undefined}
        onSavePassengers={canEdit ? savePassengersForBus : undefined}
        onConfirmJourney={canEdit ? confirmJourney : undefined}
      />
    </Box>
  );
}
